import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Calendar, AlertTriangle, Clock, CheckCircle2, ChevronRight } from 'lucide-react';

const DeadlinesWidget = () => {
  const { api } = useAuth();
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeadlines();
  }, []);

  const fetchDeadlines = async () => {
    try {
      const response = await api.get('/contracts/deadlines/all');
      setDeadlines(response.data);
    } catch (error) {
      console.error('Error fetching deadlines:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (item) => {
    if (item.is_overdue) return <AlertTriangle className="w-4 h-4 text-red-400" />;
    if (item.is_urgent) return <Clock className="w-4 h-4 text-yellow-400" />;
    return <Calendar className="w-4 h-4 text-blue-400" />;
  };

  const getStatusColor = (item) => {
    if (item.is_overdue) return 'border-red-500/30 bg-red-500/5';
    if (item.is_urgent) return 'border-yellow-500/30 bg-yellow-500/5';
    return 'border-border bg-transparent';
  };

  const getDaysLabel = (days) => {
    if (days < 0) return `Vencido hace ${Math.abs(days)} días`;
    if (days === 0) return 'Vence hoy';
    if (days === 1) return 'Vence mañana';
    return `${days} días restantes`;
  };

  const getDaysColor = (item) => {
    if (item.is_overdue) return 'text-red-400';
    if (item.is_urgent) return 'text-yellow-400';
    if (item.days_remaining <= 30) return 'text-orange-400';
    return 'text-slate-400';
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-700 rounded w-1/3"></div>
          <div className="h-12 bg-slate-700/50 rounded"></div>
          <div className="h-12 bg-slate-700/50 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl">
      <div className="p-5 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            Fechas y Vencimientos
          </h3>
          {deadlines.length > 0 && (
            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
              {deadlines.length} próximas
            </span>
          )}
        </div>
      </div>

      {deadlines.length === 0 ? (
        <div className="p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500/50 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No hay fechas próximas registradas</p>
          <p className="text-xs text-slate-500 mt-1">Agrega vencimientos desde la vista de cada contrato</p>
        </div>
      ) : (
        <div className="divide-y divide-border/50">
          {deadlines.slice(0, 5).map((item, idx) => (
            <Link
              key={idx}
              to={`/contract/${item.contract_id}`}
              className={`flex items-center justify-between p-4 border-l-2 hover:bg-slate-800/50 transition-colors ${getStatusColor(item)}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {getStatusIcon(item)}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{item.label}</p>
                  <p className="text-xs text-slate-500 truncate">{item.contract_title}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                <span className={`text-xs font-medium ${getDaysColor(item)}`}>
                  {getDaysLabel(item.days_remaining)}
                </span>
                <ChevronRight className="w-3 h-3 text-slate-600" />
              </div>
            </Link>
          ))}
          {deadlines.length > 5 && (
            <div className="p-3 text-center">
              <p className="text-xs text-slate-400">
                +{deadlines.length - 5} más fecha(s) próxima(s)
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeadlinesWidget;
